import { useApi } from '@/@vxapp/@Utils/apiClient'
import type { Ref } from 'vue'
import { useCookie } from '@/@core/composable/useCookie'
import { Cookie } from '@/@vxapp/@Enums/Cookie'
import { useAuthHelpers } from '@/@vxapp/Auth/useAuthHelpers'
import {
  beginAuthLogout,
  finishAuthLogout,
  readAuthLogoutIntent,
  withAuthLogoutLock,
} from './authRefreshCoordinator'

export interface LogoutOutcome {
  serverConfirmed: boolean
  localOnly: boolean
  message: string | null
}
export const LOGOUT_UNCONFIRMED =
  'Server logout could not be confirmed. You are signed out locally.'

export function persistRevocationCredential(headers: Record<string, string>): void {
  const headerCookie = useCookie(Cookie.Headers) as unknown as Ref<Record<string, string> | null>

  headerCookie.value = headers

  const tokenCookie = useCookie(Cookie.Token) as Ref<string | null>

  tokenCookie.value = useAuthHelpers().getAuthHeader(headers)
}

export async function revokeLoginSession(
  supported: boolean,
  readHeaders: () => Record<string, string> | null,
  intent = beginAuthLogout()
): Promise<LogoutOutcome> {
  if (!supported) {
    if (intent) finishAuthLogout(intent.generation)

    return { serverConfirmed: false, localOnly: true, message: null }
  }
  let serverConfirmed = intent?.serverConfirmed === true
  if (intent && !intent.finished && intent.deadline > Date.now()) {
    try {
      const result = await withAuthLogoutLock(async () => {
        const current = readAuthLogoutIntent()
        if (current?.generation !== intent.generation) return false
        if (current.finished) return current.serverConfirmed === true
        const headers = readHeaders()
        if (!headers || !useAuthHelpers().getAuthHeader(headers)) return false
        const timeout = Math.min(7000, intent.deadline - Date.now())
        if (timeout <= 0) return false
        const controller = new AbortController()
        const timer = setTimeout(() => controller.abort(), timeout)
        try {
          const response = await useApi.logoutCurrentSession(headers, {
            timeout,
            signal: controller.signal,
          })

          return response.status === 200 && response.data?.serviceData?.loggedOut === true
        } finally {
          clearTimeout(timer)
        }
      }, intent.deadline)

      serverConfirmed = result.acquired && result.result === true
    } catch {
      // Network, permission and malformed replies never prove revocation.
    } finally {
      finishAuthLogout(intent.generation, serverConfirmed)
    }
  }

  return { serverConfirmed, localOnly: false, message: serverConfirmed ? null : LOGOUT_UNCONFIRMED }
}
